import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { categoryVideos } from '@/mocks/videos';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const videos = slug ? (categoryVideos[slug] || []) : [];

  const displayName = currentCategory?.name || slug || '';

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: `${displayName} - ${t('site.name')}`,
        description: currentCategory?.description || `${displayName}视频分类`,
        url: `https://huangtao.com/category/${slug}`,
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://huangtao.com' },
          { '@type': 'ListItem', position: 2, name: displayName },
        ],
      }} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-24 pb-12 md:pb-16">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), path: '/' },
            { label: t('category.title'), path: '/category/dianshiju' },
            { label: displayName },
          ]}
        />

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
          {displayName}
        </h1>
        {currentCategory?.description && (
          <p className="text-sm text-foreground-500 mb-8">{currentCategory.description}</p>
        )}

        <div className="flex items-center gap-2 mb-8 overflow-x-auto scrollbar-hide pb-1">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline ${
                slug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} mr-1.5`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {videos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
            {videos.map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
            <div className="col-span-full text-center py-8 text-foreground-400 text-sm">
              — {videos.length} {t('category.videos')} —
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-film-line text-3xl"></i>
            </div>
            <p className="text-foreground-500 text-sm">{t('category.noVideos')}</p>
            <Link to="/" className="text-primary-500 text-sm font-medium mt-3 inline-block no-underline hover:text-primary-600">
              {t('category.backToHome')}
            </Link>
          </div>
        )}
      </div>
    </>
  );
}